import { z } from '@hono/zod-openapi'

// id
export const idZ = z.number().openapi({ description: 'チケットID', example: 1 })
export type id = z.infer<typeof idZ>

// ownerId
export const ownerIdZ = z.string().openapi({
  description: '対象オーナーID',
  example: '00000000-0000-0000-0000-000000000000',
})
export type ownerId = z.infer<typeof ownerIdZ>

// propertyId
export const propertyIdZ = z
  .number()
  .openapi({ description: '対象物件ID', example: 10 })
export type propertyId = z.infer<typeof propertyIdZ>

// propertyId (nullable)
export const propertyIdNullableZ = z.number().nullable().openapi({
  description: '対象物件ID。物件が特定できない場合は null',
  example: 10,
})
export type propertyIdNullable = z.infer<typeof propertyIdNullableZ>

// roomId
export const roomIdZ = z.number().nullable().openapi({
  description: '対象部屋ID。部屋が特定できない場合は null',
  example: 201,
})
export type roomId = z.infer<typeof roomIdZ>

// tenantId
export const tenantIdZ = z.number().nullable().openapi({
  description: '対象賃借人ID。賃借人が特定できない場合は null',
  example: 55,
})
export type tenantId = z.infer<typeof tenantIdZ>

// title
export const titleZ = z
  .string()
  .openapi({ description: 'チケットの件名', example: '空室の設備故障について' })
export type title = z.infer<typeof titleZ>
// 作成・更新リクエスト用。空白のみの件名は受け付けない
export const titleRequiredZ = z
  .string()
  .trim()
  .min(1)
  .openapi({ description: 'チケットの件名', example: '空室の設備故障について' })

// category
export const TICKET_CATEGORY_TOIAWASE = 'toiawase'
export const TICKET_CATEGORY_KAIYAKU_KANREN = 'kaiyaku-kanren'
export const TICKET_CATEGORY_KUREMU_HADO = 'kuremu-hado'
export const TICKET_CATEGORY_KUREMU_SOFUTO = 'kuremu-sofuto'
export const TICKET_CATEGORY_SONOTA = 'sonota'
export const TICKET_CATEGORY_SABISU_NI_TSUITE = 'sabisu-ni-tsuite'
export const TICKET_CATEGORY_SHINKI_NYUKYOSHA_SHONIN = 'shinki-nyukyosha-shonin'
export const TICKET_CATEGORY_KAIYAKU_UKETSUKE = 'kaiyaku-uketsuke'

export const TICKET_CATEGORIES = [
  { key: TICKET_CATEGORY_TOIAWASE, value: '問い合せ' },
  { key: TICKET_CATEGORY_KAIYAKU_KANREN, value: '解約関連' },
  { key: TICKET_CATEGORY_KUREMU_HADO, value: 'クレームハード' },
  { key: TICKET_CATEGORY_KUREMU_SOFUTO, value: 'クレームソフト' },
  { key: TICKET_CATEGORY_SONOTA, value: 'その他' },
  { key: TICKET_CATEGORY_SABISU_NI_TSUITE, value: 'サービスについて' },
  { key: TICKET_CATEGORY_SHINKI_NYUKYOSHA_SHONIN, value: '新規入居者承認' },
  { key: TICKET_CATEGORY_KAIYAKU_UKETSUKE, value: '解約受付' },
] as const

export const categoryZ = z
  .enum([
    TICKET_CATEGORY_TOIAWASE,
    TICKET_CATEGORY_KAIYAKU_KANREN,
    TICKET_CATEGORY_KUREMU_HADO,
    TICKET_CATEGORY_KUREMU_SOFUTO,
    TICKET_CATEGORY_SONOTA,
    TICKET_CATEGORY_SABISU_NI_TSUITE,
    TICKET_CATEGORY_SHINKI_NYUKYOSHA_SHONIN,
    TICKET_CATEGORY_KAIYAKU_UKETSUKE,
  ])
  .openapi({
    description:
      'カテゴリ（toiawase:問い合せ / kaiyaku-kanren:解約関連 / kuremu-hado:クレームハード / kuremu-sofuto:クレームソフト / sonota:その他 / sabisu-ni-tsuite:サービスについて / shinki-nyukyosha-shonin:新規入居者承認 / kaiyaku-uketsuke:解約受付）',
    example: TICKET_CATEGORY_TOIAWASE,
  })
export type category = z.infer<typeof categoryZ>

// subCategory
export const TICKET_SUBCATEGORY_KAGI_AUTOROKKU = 'kagi-autorokku'
export const TICKET_SUBCATEGORY_MERUBOKKUSU_TAKUHAI_BOKKUSU =
  'merubokkusu-takuhai-bokkusu'
export const TICKET_SUBCATEGORY_INTANETTO_TEREBI_DENWA =
  'intanetto-terebi-denwa'
export const TICKET_SUBCATEGORY_EAKON_KANREN = 'eakon-kanren'
export const TICKET_SUBCATEGORY_SETSUBI_KANREN_SHIYO_HOHO_TO =
  'setsubi-kanren-shiyo-hoho-to'
export const TICKET_SUBCATEGORY_CHUSHAJO_CHURINJO = 'chushajo-churinjo'
export const TICKET_SUBCATEGORY_YACHIN_SUIDO_DAI = 'yachin-suido-dai'
export const TICKET_SUBCATEGORY_HOSHO_GAISHA = 'hosho-gaisha'
export const TICKET_SUBCATEGORY_SHORUI_KANKEI = 'shorui-kankei'
export const TICKET_SUBCATEGORY_HOKEN = 'hoken'
export const TICKET_SUBCATEGORY_KOSHIN_SAIKEIYAKU = 'koshin-saikeiyaku'
export const TICKET_SUBCATEGORY_MEIGI_HENKO = 'meigi-henko'
export const TICKET_SUBCATEGORY_NYUKYOSHA_JOHO_HENKO = 'nyukyosha-joho-henko'
export const TICKET_SUBCATEGORY_RAIFURAIN = 'raifurain'
export const TICKET_SUBCATEGORY_KYOJU_RURU = 'kyoju-ruru'
export const TICKET_SUBCATEGORY_ANPI_KYOJU_KAKUNIN = 'anpi-kyoju-kakunin'
export const TICKET_SUBCATEGORY_SONOTA_TOIAWASE = 'sonota-toiawase'

export const TICKET_SUBCATEGORY_KAIYAKU_TETSUZUKI_HOHO =
  'kaiyaku-tetsuzuki-hoho'
export const TICKET_SUBCATEGORY_KAIYAKU_BI_HENKO_TORIKESHI =
  'kaiyaku-bi-henko-torikeshi'
export const TICKET_SUBCATEGORY_TAIKYO_TACHIAI = 'taikyo-tachiai'
export const TICKET_SUBCATEGORY_TAIKYO_SEISAN = 'taikyo-seisan'
export const TICKET_SUBCATEGORY_SHIKIKIN_HENKIN_KOZA_TENKYO_SAKI_JUSHO =
  'shikikin-henkin-koza-tenkyo-saki-jusho'

export const TICKET_SUBCATEGORY_EAKON = 'eakon'
export const TICKET_SUBCATEGORY_KYUTOKI = 'kyutoki'
export const TICKET_SUBCATEGORY_NYUKYOJI_FUGUAI = 'nyukyoji-fuguai'
export const TICKET_SUBCATEGORY_MIZUMAWARI = 'mizumawari'
export const TICKET_SUBCATEGORY_TOIRE_WOSHURETTO = 'toire-woshuretto'
export const TICKET_SUBCATEGORY_ROSUI = 'rosui'
export const TICKET_SUBCATEGORY_INTAHON_CHAIMU = 'intahon-chaimu'
export const TICKET_SUBCATEGORY_KONRO = 'konro'
export const TICKET_SUBCATEGORY_TATEGU = 'tategu'
export const TICKET_SUBCATEGORY_TEREBI = 'terebi'
export const TICKET_SUBCATEGORY_INTANETTO = 'intanetto'
export const TICKET_SUBCATEGORY_KYOYOBU = 'kyoyobu'
export const TICKET_SUBCATEGORY_KAGI_GENKAN = 'kagi-genkan'
export const TICKET_SUBCATEGORY_KANKI_KANREN = 'kanki-kanren'
export const TICKET_SUBCATEGORY_SHOMEI_KIGU = 'shomei-kigu'
export const TICKET_SUBCATEGORY_BUREKA_DENKI_KANREN = 'bureka-denki-kanren'
export const TICKET_SUBCATEGORY_KEIHOKI = 'keihoki'
export const TICKET_SUBCATEGORY_KUROSU_YUKA_TATAMI = 'kurosu-yuka-tatami'
export const TICKET_SUBCATEGORY_KABI_KETSURO = 'kabi-ketsuro'

export const TICKET_SUBCATEGORY_CHUSHAJO = 'chushajo'
export const TICKET_SUBCATEGORY_CHURINJO = 'churinjo'
export const TICKET_SUBCATEGORY_SO_ON = 'so-on'
export const TICKET_SUBCATEGORY_GOMI = 'gomi'
export const TICKET_SUBCATEGORY_SHOKUBUTSU = 'shokubutsu'
export const TICKET_SUBCATEGORY_SEIBUTSU = 'seibutsu'
export const TICKET_SUBCATEGORY_KITSUEN = 'kitsuen'
export const TICKET_SUBCATEGORY_NIOI = 'nioi'
export const TICKET_SUBCATEGORY_KYOYOBU_NO_SHIYO_NI_TSUITE_SHIBUTSU =
  'kyoyobu-no-shiyo-ni-tsuite-shibutsu'
export const TICKET_SUBCATEGORY_KYOUBU_NO_YOGORE_SEISO =
  'kyoyobu-no-yogore-seiso'

export const TICKET_SUBCATEGORY_SONOTA = 'sonota'
export const TICKET_SUBCATEGORY_SABISU_TOIAWASE = 'sabisu-toiawase'
export const TICKET_SUBCATEGORY_SHINKI_KEIYAKU_SHONIN_KAKUNIN =
  'shinki-keiyaku-shonin-kakunin'
export const TICKET_SUBCATEGORY_KAIYAKU_UKETSUKE_KAKUNIN =
  'kaiyaku-uketsuke-kakunin'

export const TICKET_SUBCATEGORIES = [
  // 問い合せ
  { key: TICKET_SUBCATEGORY_KAGI_AUTOROKKU, value: '鍵・オートロック' },
  {
    key: TICKET_SUBCATEGORY_MERUBOKKUSU_TAKUHAI_BOKKUSU,
    value: 'メールボックス・宅配ボックス',
  },
  {
    key: TICKET_SUBCATEGORY_INTANETTO_TEREBI_DENWA,
    value: 'インターネット・テレビ・電話',
  },
  { key: TICKET_SUBCATEGORY_EAKON_KANREN, value: 'エアコン関連' },
  {
    key: TICKET_SUBCATEGORY_SETSUBI_KANREN_SHIYO_HOHO_TO,
    value: '設備関連（使用方法等）',
  },
  { key: TICKET_SUBCATEGORY_CHUSHAJO_CHURINJO, value: '駐車場・駐輪場' },
  { key: TICKET_SUBCATEGORY_YACHIN_SUIDO_DAI, value: '家賃・水道代' },
  { key: TICKET_SUBCATEGORY_HOSHO_GAISHA, value: '保証会社' },
  { key: TICKET_SUBCATEGORY_SHORUI_KANKEI, value: '書類関係' },
  { key: TICKET_SUBCATEGORY_HOKEN, value: '保険' },
  { key: TICKET_SUBCATEGORY_KOSHIN_SAIKEIYAKU, value: '更新・再契約' },
  { key: TICKET_SUBCATEGORY_MEIGI_HENKO, value: '名義変更' },
  { key: TICKET_SUBCATEGORY_NYUKYOSHA_JOHO_HENKO, value: '入居者情報変更' },
  { key: TICKET_SUBCATEGORY_RAIFURAIN, value: 'ライフライン' },
  { key: TICKET_SUBCATEGORY_KYOJU_RURU, value: '居住ルール' },
  { key: TICKET_SUBCATEGORY_ANPI_KYOJU_KAKUNIN, value: '安否・居住確認' },
  { key: TICKET_SUBCATEGORY_SONOTA_TOIAWASE, value: 'その他' },

  // 解約関連
  { key: TICKET_SUBCATEGORY_KAIYAKU_TETSUZUKI_HOHO, value: '解約手続き方法' },
  {
    key: TICKET_SUBCATEGORY_KAIYAKU_BI_HENKO_TORIKESHI,
    value: '解約日変更・取消',
  },
  { key: TICKET_SUBCATEGORY_TAIKYO_TACHIAI, value: '退去立会い' },
  { key: TICKET_SUBCATEGORY_TAIKYO_SEISAN, value: '退去精算' },
  {
    key: TICKET_SUBCATEGORY_SHIKIKIN_HENKIN_KOZA_TENKYO_SAKI_JUSHO,
    value: '敷金返金口座・転居先住所',
  },

  // クレームハード
  { key: TICKET_SUBCATEGORY_EAKON, value: 'エアコン' },
  { key: TICKET_SUBCATEGORY_KYUTOKI, value: '給湯器' },
  {
    key: TICKET_SUBCATEGORY_NYUKYOJI_FUGUAI,
    value: '入居時不具合',
  },
  { key: TICKET_SUBCATEGORY_MIZUMAWARI, value: '水回り' },
  {
    key: TICKET_SUBCATEGORY_TOIRE_WOSHURETTO,
    value: 'トイレ・ウォシュレット',
  },
  { key: TICKET_SUBCATEGORY_ROSUI, value: '漏水' },
  {
    key: TICKET_SUBCATEGORY_INTAHON_CHAIMU,
    value: 'インターホン・チャイム',
  },
  { key: TICKET_SUBCATEGORY_KONRO, value: 'コンロ' },
  { key: TICKET_SUBCATEGORY_TATEGU, value: '建具' },
  { key: TICKET_SUBCATEGORY_TEREBI, value: 'テレビ' },
  { key: TICKET_SUBCATEGORY_INTANETTO, value: 'インターネット' },
  { key: TICKET_SUBCATEGORY_KYOYOBU, value: '共用部' },
  { key: TICKET_SUBCATEGORY_KAGI_GENKAN, value: '鍵（玄関）' },
  { key: TICKET_SUBCATEGORY_KANKI_KANREN, value: '換気関連' },
  { key: TICKET_SUBCATEGORY_SHOMEI_KIGU, value: '照明器具' },
  {
    key: TICKET_SUBCATEGORY_BUREKA_DENKI_KANREN,
    value: 'ブレーカー・電気関連',
  },
  { key: TICKET_SUBCATEGORY_KEIHOKI, value: '警報器' },
  {
    key: TICKET_SUBCATEGORY_KUROSU_YUKA_TATAMI,
    value: 'クロス・床・畳',
  },
  { key: TICKET_SUBCATEGORY_KABI_KETSURO, value: 'カビ・結露' },

  // クレームソフト
  { key: TICKET_SUBCATEGORY_CHUSHAJO, value: '駐車場' },
  { key: TICKET_SUBCATEGORY_CHURINJO, value: '駐輪場' },
  { key: TICKET_SUBCATEGORY_SO_ON, value: '騒音' },
  { key: TICKET_SUBCATEGORY_GOMI, value: 'ゴミ' },
  { key: TICKET_SUBCATEGORY_SHOKUBUTSU, value: '植物' },
  { key: TICKET_SUBCATEGORY_SEIBUTSU, value: '生物' },
  { key: TICKET_SUBCATEGORY_KITSUEN, value: '喫煙' },
  { key: TICKET_SUBCATEGORY_NIOI, value: 'におい' },
  {
    key: TICKET_SUBCATEGORY_KYOYOBU_NO_SHIYO_NI_TSUITE_SHIBUTSU,
    value: '共用部の使用について（私物）',
  },
  {
    key: TICKET_SUBCATEGORY_KYOUBU_NO_YOGORE_SEISO,
    value: '共用部の汚れ・清掃',
  },

  // その他
  { key: TICKET_SUBCATEGORY_SONOTA, value: 'その他' },
  // サービスについて
  { key: TICKET_SUBCATEGORY_SABISU_TOIAWASE, value: 'サービス問合せ' },
  // 新規入居者承認
  {
    key: TICKET_SUBCATEGORY_SHINKI_KEIYAKU_SHONIN_KAKUNIN,
    value: '新規契約承認確認',
  },
  // 解約受付
  { key: TICKET_SUBCATEGORY_KAIYAKU_UKETSUKE_KAKUNIN, value: '解約受付確認' },
] as const

export const subCategoryZ = z
  .enum([
    // 問い合せ
    TICKET_SUBCATEGORY_KAGI_AUTOROKKU,
    TICKET_SUBCATEGORY_MERUBOKKUSU_TAKUHAI_BOKKUSU,
    TICKET_SUBCATEGORY_INTANETTO_TEREBI_DENWA,
    TICKET_SUBCATEGORY_EAKON_KANREN,
    TICKET_SUBCATEGORY_SETSUBI_KANREN_SHIYO_HOHO_TO,
    TICKET_SUBCATEGORY_CHUSHAJO_CHURINJO,
    TICKET_SUBCATEGORY_YACHIN_SUIDO_DAI,
    TICKET_SUBCATEGORY_HOSHO_GAISHA,
    TICKET_SUBCATEGORY_SHORUI_KANKEI,
    TICKET_SUBCATEGORY_HOKEN,
    TICKET_SUBCATEGORY_KOSHIN_SAIKEIYAKU,
    TICKET_SUBCATEGORY_MEIGI_HENKO,
    TICKET_SUBCATEGORY_NYUKYOSHA_JOHO_HENKO,
    TICKET_SUBCATEGORY_RAIFURAIN,
    TICKET_SUBCATEGORY_KYOJU_RURU,
    TICKET_SUBCATEGORY_ANPI_KYOJU_KAKUNIN,
    TICKET_SUBCATEGORY_SONOTA_TOIAWASE,
    // 解約関連
    TICKET_SUBCATEGORY_KAIYAKU_TETSUZUKI_HOHO,
    TICKET_SUBCATEGORY_KAIYAKU_BI_HENKO_TORIKESHI,
    TICKET_SUBCATEGORY_TAIKYO_TACHIAI,
    TICKET_SUBCATEGORY_TAIKYO_SEISAN,
    TICKET_SUBCATEGORY_SHIKIKIN_HENKIN_KOZA_TENKYO_SAKI_JUSHO,
    // クレームハード
    TICKET_SUBCATEGORY_EAKON,
    TICKET_SUBCATEGORY_KYUTOKI,
    TICKET_SUBCATEGORY_NYUKYOJI_FUGUAI,
    TICKET_SUBCATEGORY_MIZUMAWARI,
    TICKET_SUBCATEGORY_TOIRE_WOSHURETTO,
    TICKET_SUBCATEGORY_ROSUI,
    TICKET_SUBCATEGORY_INTAHON_CHAIMU,
    TICKET_SUBCATEGORY_KONRO,
    TICKET_SUBCATEGORY_TATEGU,
    TICKET_SUBCATEGORY_TEREBI,
    TICKET_SUBCATEGORY_INTANETTO,
    TICKET_SUBCATEGORY_KYOYOBU,
    TICKET_SUBCATEGORY_KAGI_GENKAN,
    TICKET_SUBCATEGORY_KANKI_KANREN,
    TICKET_SUBCATEGORY_SHOMEI_KIGU,
    TICKET_SUBCATEGORY_BUREKA_DENKI_KANREN,
    TICKET_SUBCATEGORY_KEIHOKI,
    TICKET_SUBCATEGORY_KUROSU_YUKA_TATAMI,
    TICKET_SUBCATEGORY_KABI_KETSURO,
    // クレームソフト
    TICKET_SUBCATEGORY_CHUSHAJO,
    TICKET_SUBCATEGORY_CHURINJO,
    TICKET_SUBCATEGORY_SO_ON,
    TICKET_SUBCATEGORY_GOMI,
    TICKET_SUBCATEGORY_SHOKUBUTSU,
    TICKET_SUBCATEGORY_SEIBUTSU,
    TICKET_SUBCATEGORY_KITSUEN,
    TICKET_SUBCATEGORY_NIOI,
    TICKET_SUBCATEGORY_KYOYOBU_NO_SHIYO_NI_TSUITE_SHIBUTSU,
    TICKET_SUBCATEGORY_KYOUBU_NO_YOGORE_SEISO,
    // その他
    TICKET_SUBCATEGORY_SONOTA,
    // サービスについて
    TICKET_SUBCATEGORY_SABISU_TOIAWASE,
    // 新規入居者承認
    TICKET_SUBCATEGORY_SHINKI_KEIYAKU_SHONIN_KAKUNIN,
    // 解約受付
    TICKET_SUBCATEGORY_KAIYAKU_UKETSUKE_KAKUNIN,
  ])
  .openapi({
    description: 'サブカテゴリ。category に紐づく詳細分類',
    example: TICKET_SUBCATEGORY_KAGI_AUTOROKKU,
  })
export type subCategory = z.infer<typeof subCategoryZ>

// content
export const contentZ = z.string().max(1000).openapi({
  description: '問い合わせ・依頼の内容',
  example: 'エアコンの修理について',
})
export type content = z.infer<typeof contentZ>

// status
export const TICKET_STATUS_OC_IN_PROGRESS = 'oc_in_progress'
export const TICKET_STATUS_OWNER_IN_PROGRESS = 'owner_in_progress'
export const TICKET_STATUS_CONTRACTOR_IN_PROGRESS = 'contractor_in_progress'
export const TICKET_STATUS_COMPLETED = 'completed'
export const TICKET_STATUSES = [
  { key: TICKET_STATUS_OC_IN_PROGRESS, value: 'OC対応中' },
  { key: TICKET_STATUS_OWNER_IN_PROGRESS, value: 'オーナー対応中' },
  { key: TICKET_STATUS_CONTRACTOR_IN_PROGRESS, value: '業者対応中' },
  { key: TICKET_STATUS_COMPLETED, value: '完了' },
] as const
export const statusZ = z
  .enum([
    TICKET_STATUS_OC_IN_PROGRESS,
    TICKET_STATUS_OWNER_IN_PROGRESS,
    TICKET_STATUS_CONTRACTOR_IN_PROGRESS,
    TICKET_STATUS_COMPLETED,
  ])
  .openapi({
    description:
      '対応ステータス（oc_in_progress:OC対応中 / owner_in_progress:オーナー対応中 / contractor_in_progress:業者対応中 / completed:完了）',
    example: TICKET_STATUS_OC_IN_PROGRESS,
  })
export type status = z.infer<typeof statusZ>

// occurredAt
export const occurredAtStringZ = z.string().openapi({
  description: '事象の発生日時（ISO8601）',
  example: '2024-04-01T12:00:00.000Z',
})
export type occurredAtString = z.infer<typeof occurredAtStringZ>

export const occurredAtDateZ = z
  .date()
  .openapi({ example: '2024-04-01T12:00:00.000Z' })
export type occurredAtDate = z.infer<typeof occurredAtDateZ>

// alertAt
export const alertAtStringZ = z.string().nullable().openapi({
  description: 'アラートを設定した日時（ISO8601）。null はアラートなし',
  example: '2024-04-02T10:00:00.000Z',
})
export type alertAtString = z.infer<typeof alertAtStringZ>

export const alertAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-04-02T10:00:00.000Z' })
export type alertAtDate = z.infer<typeof alertAtDateZ>

// alertComment
export const alertCommentZ = z
  .string()
  .max(1000)
  .openapi({ description: 'アラートの内容', example: 'アラートコメント' })
export type alertComment = z.infer<typeof alertCommentZ>

// ocComment
export const ocCommentZ = z.string().max(1000).openapi({
  description: 'OC（オペレーションセンター）の対応コメント',
  example: 'OCコメント',
})
export type ocComment = z.infer<typeof ocCommentZ>

// ocAssigneeId
export const ocAssigneeIdZ = z.string().nullable().openapi({
  description: 'OC担当者の管理者ID',
  example: 'a4f2f2e6-2d5a-4c5f-a2dd-1234567890ab',
})
export type ocAssigneeId = z.infer<typeof ocAssigneeIdZ>

// contactType
export const CONTACT_TYPE_PHONE = 'phone'
export const CONTACT_TYPE_EMAIL = 'email'
export const CONTACT_TYPE_SMS = 'sms'
export const CONTACT_TYPE_CHAT = 'chat'
export const CONTACT_TYPE_FAX = 'fax'
export const CONTACT_TYPE_OTHER = 'other'
export const CONTACT_TYPES = [
  { key: CONTACT_TYPE_PHONE, value: '電話' },
  { key: CONTACT_TYPE_EMAIL, value: 'メール' },
  { key: CONTACT_TYPE_SMS, value: 'SMS' },
  { key: CONTACT_TYPE_CHAT, value: 'チャット' },
  { key: CONTACT_TYPE_FAX, value: 'FAX' },
  { key: CONTACT_TYPE_OTHER, value: 'その他' },
] as const
export const contactTypeZ = z
  .enum([
    CONTACT_TYPE_PHONE,
    CONTACT_TYPE_EMAIL,
    CONTACT_TYPE_SMS,
    CONTACT_TYPE_CHAT,
    CONTACT_TYPE_FAX,
    CONTACT_TYPE_OTHER,
  ])
  .openapi({
    description:
      '連絡手段（phone:電話 / email:メール / sms:SMS / chat:チャット / fax:FAX / other:その他）',
    example: CONTACT_TYPE_PHONE,
  })
export type contactType = z.infer<typeof contactTypeZ>

// contactFromCategory
export const CONTACT_CATEGORY_OWNER = 'owner'
export const CONTACT_CATEGORY_TENANT = 'tenant'
export const CONTACT_CATEGORY_RESIDENT = 'resident'
export const CONTACT_CATEGORY_OC = 'oc'
export const CONTACT_CATEGORY_CONTRACTOR = 'contractor'
export const CONTACT_CATEGORY_EMERGENCY = 'emergency'
export const CONTACT_CATEGORY_OTHER = 'other'
export const CONTACT_CATEGORIES = [
  { key: CONTACT_CATEGORY_OWNER, value: 'オーナー' },
  { key: CONTACT_CATEGORY_TENANT, value: '賃借人' },
  { key: CONTACT_CATEGORY_RESIDENT, value: '入居者（賃借人以外）' },
  { key: CONTACT_CATEGORY_OC, value: 'OC' },
  { key: CONTACT_CATEGORY_CONTRACTOR, value: '業者' },
  { key: CONTACT_CATEGORY_EMERGENCY, value: '緊急連絡センター' },
  { key: CONTACT_CATEGORY_OTHER, value: 'その他' },
] as const
export const contactFromCategoryZ = z
  .enum([
    CONTACT_CATEGORY_OWNER,
    CONTACT_CATEGORY_TENANT,
    CONTACT_CATEGORY_RESIDENT,
    CONTACT_CATEGORY_OC,
    CONTACT_CATEGORY_CONTRACTOR,
    CONTACT_CATEGORY_EMERGENCY,
    CONTACT_CATEGORY_OTHER,
  ])
  .openapi({
    description:
      '連絡元の区分（owner:オーナー / tenant:賃借人 / resident:入居者(賃借人以外) / oc:OC / contractor:業者 / emergency:緊急連絡センター / other:その他）',
    example: CONTACT_CATEGORY_OWNER,
  })
export type contactFromCategory = z.infer<typeof contactFromCategoryZ>

// contactFromOwnerId
export const contactFromOwnerIdZ = z.string().nullable().openapi({
  description: '連絡元がオーナーの場合のオーナーID',
  example: '00000000-0000-0000-0000-000000000000',
})
export type contactFromOwnerId = z.infer<typeof contactFromOwnerIdZ>

// contactFromTenantId
export const contactFromTenantIdZ = z
  .number()
  .nullable()
  .openapi({ description: '連絡元が賃借人の場合の賃借人ID', example: 55 })
export type contactFromTenantId = z.infer<typeof contactFromTenantIdZ>

// contactFromAdminId
export const contactFromAdminIdZ = z.string().nullable().openapi({
  description: '連絡元が管理者の場合の管理者ID',
  example: 'a4f2f2e6-2d5a-4c5f-a2dd-1234567890ab',
})
export type contactFromAdminId = z.infer<typeof contactFromAdminIdZ>

// contactFromText
export const contactFromTextZ = z.string().max(255).nullable().openapi({
  description: '連絡元をIDで특定できない場合の自由記述',
  example: '連絡者テキスト',
})
export type contactFromText = z.infer<typeof contactFromTextZ>

// contactToCategory
export const contactToCategoryZ = z
  .enum([
    CONTACT_CATEGORY_OWNER,
    CONTACT_CATEGORY_TENANT,
    CONTACT_CATEGORY_RESIDENT,
    CONTACT_CATEGORY_OC,
    CONTACT_CATEGORY_CONTRACTOR,
    CONTACT_CATEGORY_EMERGENCY,
    CONTACT_CATEGORY_OTHER,
  ])
  .openapi({
    description:
      '連絡先の区分（owner:オーナー / tenant:賃借人 / resident:入居者(賃借人以外) / oc:OC / contractor:業者 / emergency:緊急連絡センター / other:その他）',
    example: CONTACT_CATEGORY_TENANT,
  })
export type contactToCategory = z.infer<typeof contactToCategoryZ>

// contactToOwnerId
export const contactToOwnerIdZ = z.string().nullable().openapi({
  description: '連絡先がオーナーの場合のオーナーID',
  example: '00000000-0000-0000-0000-000000000000',
})
export type contactToOwnerId = z.infer<typeof contactToOwnerIdZ>

// contactToTenantId
export const contactToTenantIdZ = z
  .number()
  .nullable()
  .openapi({ description: '連絡先が賃借人の場合の賃借人ID', example: 55 })
export type contactToTenantId = z.infer<typeof contactToTenantIdZ>

// contactToAdminId
export const contactToAdminIdZ = z.string().nullable().openapi({
  description: '連絡先が管理者の場合の管理者ID',
  example: 'a4f2f2e6-2d5a-4c5f-a2dd-1234567890ab',
})
export type contactToAdminId = z.infer<typeof contactToAdminIdZ>

// contactToText
export const contactToTextZ = z.string().max(255).nullable().openapi({
  description: '連絡先をIDで特定できない場合の自由記述',
  example: '連絡先テキスト',
})
export type contactToText = z.infer<typeof contactToTextZ>

// note
export const noteZ = z
  .string()
  .max(1000)
  .openapi({ description: '備考', example: '備考' })
export type note = z.infer<typeof noteZ>

// isMessageReadByAdmin
export const isMessageReadByAdminZ = z.boolean().openapi({
  example: false,
  description: '管理者がチケットのメッセージを読んだかどうか',
})
export type isMessageReadByAdmin = z.infer<typeof isMessageReadByAdminZ>

// lastAdminMessageNotificationSentAt
export const lastAdminMessageNotificationSentAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-04-02T10:00:00.000Z' })
export type lastAdminMessageNotificationSentAtDate = z.infer<
  typeof lastAdminMessageNotificationSentAtDateZ
>

// createdOwnerId
export const createdOwnerIdZ = z.string().nullable().openapi({
  description: 'チケットを起票したオーナーID。管理者起票の場合は null',
  example: '00000000-0000-0000-0000-000000000000',
})
export type createdOwnerId = z.infer<typeof createdOwnerIdZ>

// createdAdminId
export const createdAdminIdZ = z.string().nullable().openapi({
  description: 'チケットを起票した管理者ID。オーナー起票の場合は null',
  example: '00000000-0000-0000-0000-000000000001',
})
export type createdAdminId = z.infer<typeof createdAdminIdZ>

// createdAt
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-04-01T12:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-04-01T12:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-04-02T10:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-04-02T10:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// reason
export const reasonZ = z
  .string()
  .min(1)
  .max(255)
  .openapi({ example: '対応完了のため' })
export type reason = z.infer<typeof reasonZ>

// contractorStatus
export const CONTRACTOR_STATUS_ESTIMATE_REQUESTED = 'estimate_requested'
export const CONTRACTOR_STATUS_WORK_REQUESTED = 'work_requested'
export const CONTRACTOR_STATUS_ORDER_REQUESTED = 'order_requested'
export const CONTRACTOR_STATUS_ESTIMATE_RECEIVED = 'estimate_received'
export const CONTRACTOR_STATUS_OWNER_CONFIRMING = 'owner_confirming'
export const CONTRACTOR_STATUS_COMPLETED = 'completed'
export const CONTRACTOR_STATUSES = [
  { key: CONTRACTOR_STATUS_ESTIMATE_REQUESTED, value: '見積依頼中' },
  { key: CONTRACTOR_STATUS_WORK_REQUESTED, value: '業務依頼済' },
  { key: CONTRACTOR_STATUS_ORDER_REQUESTED, value: '発注依頼済' },
  { key: CONTRACTOR_STATUS_ESTIMATE_RECEIVED, value: '見積受領済' },
  { key: CONTRACTOR_STATUS_OWNER_CONFIRMING, value: 'オーナー確認中' },
  { key: CONTRACTOR_STATUS_COMPLETED, value: '完了' },
] as const
export const contractorStatusZ = z
  .enum([
    CONTRACTOR_STATUS_ESTIMATE_REQUESTED,
    CONTRACTOR_STATUS_WORK_REQUESTED,
    CONTRACTOR_STATUS_ORDER_REQUESTED,
    CONTRACTOR_STATUS_ESTIMATE_RECEIVED,
    CONTRACTOR_STATUS_OWNER_CONFIRMING,
    CONTRACTOR_STATUS_COMPLETED,
  ])
  .nullable()
  .openapi({
    description:
      '業者対応の進捗（estimate_requested:見積依頼中 / work_requested:業務依頼済 / order_requested:発注依頼済 / estimate_received:見積受領済 / owner_confirming:オーナー確認中 / completed:完了）',
    example: CONTRACTOR_STATUS_ESTIMATE_REQUESTED,
  })
export type contractorStatus = z.infer<typeof contractorStatusZ>

// contractorType
export const CONTRACTOR_TYPE_AGENTCALL = 'agentcall'
export const CONTRACTOR_TYPE_OWNER_SPECIFIED = 'owner_specified'
export const CONTRACTOR_TYPES = [
  { key: CONTRACTOR_TYPE_AGENTCALL, value: 'エージェントコール業者' },
  { key: CONTRACTOR_TYPE_OWNER_SPECIFIED, value: 'オーナー様指定業者' },
] as const
export const contractorTypeZ = z
  .enum([CONTRACTOR_TYPE_AGENTCALL, CONTRACTOR_TYPE_OWNER_SPECIFIED])
  .nullable()
  .openapi({
    description:
      '手配した業者の区分（agentcall:エージェントコール業者 / owner_specified:オーナー様指定業者）',
    example: CONTRACTOR_TYPE_AGENTCALL,
  })
export type contractorType = z.infer<typeof contractorTypeZ>

// contractorName
export const contractorNameZ = z
  .string()
  .max(255)
  .openapi({ description: '手配した業者名', example: '株式会社サンプル業者' })
export type contractorName = z.infer<typeof contractorNameZ>

// contractorContact
export const contractorContactZ = z
  .string()
  .max(255)
  .openapi({ description: '手配した業者の担当者', example: '田中太郎' })
export type contractorContact = z.infer<typeof contractorContactZ>

// contractorContactInfo
export const contractorContactInfoZ = z.string().max(500).openapi({
  description: '手配した業者の連絡先',
  example: '03-1234-5678',
})
export type contractorContactInfo = z.infer<typeof contractorContactInfoZ>

// contractorScheduledDate
export const contractorScheduledDateZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .openapi({
    description: '業者の対応予定日（YYYY-MM-DD）',
    example: '2024-04-15',
  })
export type contractorScheduledDate = z.infer<typeof contractorScheduledDateZ>

// contractorNote
export const contractorNoteZ = z
  .string()
  .max(1000)
  .openapi({ description: '業者対応に関する備考', example: '業者備考' })
export type contractorNote = z.infer<typeof contractorNoteZ>

// echoesCaseId（Echoesが採番した案件ID）
export const echoesCaseIdZ = z
  .number()
  .int()
  .nullable()
  .openapi({ example: 456 })
export type echoesCaseId = z.infer<typeof echoesCaseIdZ>
